import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import * as Sentry from "@sentry/react";
import {
    finishAssistantMessage,
    removeThread,
    threadsSelectors,
    updateThreadName,
} from "./threadsSlice";
import {
    ChatEngine,
    ChatStopReason,
    messageTypeToEnum,
    KAIResponseType,
    RagProcessMessage,
    RagSourceMessage,
    RateMessageRequest,
    RedisPagedRequest,
    System,
    ThreadInfo,
    ThreadWithMetadata,
    PaginatedResponse,
    ThreadSearchResult,
    ThreadSearchResultRaw,
    QuickSearchResult,
    QuickSearchRequest,
    PostSearchRequest,
    PostSearchResponse,
    ThreadPostSearch,
    TriggerSearchResult,
    InitResponse,
    UserSettingsResponse,
    UpdateUserSettingsRequest,
    ApiLanguageCode,
    LanguageEnum,
    getLangCodeFromEnum,
    getApiLanguageCode,
    SeedMessage,
    LocalStorageKeys,
} from "../../types";
import {bffApi} from "./bffApi";
import {readLocalStorageValue} from "@mantine/hooks";
import {
    handleMessage,
    MessageHandlerContext,
    MessageHandlerResult,
} from "./messageHandlers";
import {classifyStreamError} from "./utils/streamErrorClassification";
import {RootState} from "../../app/store";
import {remark} from "remark";
import stripMarkdown from "strip-markdown";

interface SendMessageRequest {
    system: System;
    streamId: string;
    threadId?: string | null;
    message?: string;
    messageId?: number;
    responseType?: KAIResponseType;
    threadSeed?: SeedMessage[];
    onThreadIdReceived?: (threadId: string) => void;
}

interface MessageStreamResponse {
    message: string;
    processes: RagProcessMessage[];
    sources: RagSourceMessage[];
    done: boolean;
}

const getApiEndpoint = () => {
    const chatEngine = readLocalStorageValue<ChatEngine>({
        key: LocalStorageKeys.ChatEngine,
        defaultValue: ChatEngine.Standard,
    });

    if (chatEngine === ChatEngine.Experimental) {
        return "/api/semantic/completion";
    }
    return "/api/Completion/completion";
};

const getParsedMessage = (message: string) => {
    try {
        return JSON.parse(message);
    } catch (error) {
        // Don't log this error, it's mostly noise
        // console.error("Could not JSON parse stream message", message, error);
        return null;
    }
};

const stateResponseTypeToApiResponseType = (
    stateResponseType: KAIResponseType
) => {
    return stateResponseType === KAIResponseType.Fast ? 0 : 1;
};

const streamControllers = new Map<string, AbortController>();

export const abortStream = (streamId: string) => {
    const controller = streamControllers.get(streamId);
    if (controller) {
        controller.abort();
        streamControllers.delete(streamId);
    }
};

export const getActiveStreamCount = () => streamControllers.size;

const tryParseJson = (json: string) => {
    try {
        return JSON.parse(json);
    } catch (error) {
        return json;
    }
};

export const chatApi = createApi({
    reducerPath: "chatApi",
    baseQuery: fetchBaseQuery({baseUrl: "/api"}),
    tagTypes: ["MessageStream", "Threads", "Thread", "SearchThreads", "Init"],
    endpoints: (builder) => ({
        sendMessage: builder.mutation<
            MessageStreamResponse,
            SendMessageRequest
        >({
            queryFn: () => ({
                data: {
                    message: "",
                    processes: [],
                    sources: [],
                    done: false,
                },
            }),
            async onQueryStarted(
                {
                    system,
                    streamId,
                    threadId,
                    message,
                    messageId,
                    responseType,
                    threadSeed,
                    onThreadIdReceived,
                },
                {dispatch, queryFulfilled, getState}
            ) {
                if (!system) {
                    throw new Error("System configuration is not set.");
                }

                // Optimistically update the thread's lastUpdated timestamp in getUserThreads cache
                // Note: We don't update searchUserThreads cache since it's keyed by query string
                // and would require updating multiple cache entries with unknown query keys
                if (threadId) {
                    // Since getUserThreads uses serializeQueryArgs to maintain a single cache entry,
                    // we can update it with any valid RedisPagedRequest parameters
                    dispatch(
                        chatApi.util.updateQueryData(
                            "getUserThreads",
                            {page: 0, pageSize: 20},
                            (draft) => {
                                const threadIndex = draft.items.findIndex(
                                    (thread) => thread.threadId === threadId
                                );
                                if (threadIndex !== -1) {
                                    draft.items[threadIndex].lastUpdated =
                                        new Date().toISOString();
                                }
                            }
                        )
                    );
                }

                const stateResponseType =
                    responseType ??
                    (getState() as RootState).session.chosenResponseType;

                let accumulatedText = "";
                let accumulatedDispatched = false;
                const streamController = new AbortController();
                streamControllers.set(streamId, streamController);
                let stopReason: ChatStopReason = ChatStopReason.Stop;
                let firstTokenEmitted = false;
                const streamStart = performance.now();
                const chatEngine = readLocalStorageValue<ChatEngine>({
                    key: LocalStorageKeys.ChatEngine,
                    defaultValue: ChatEngine.Standard,
                });
                Sentry.metrics.count("chat.message_sent", 1, {
                    attributes: {
                        engine: chatEngine,
                        responseType: stateResponseType,
                    },
                });
                Sentry.metrics.distribution(
                    "chat.message_length_chars",
                    message?.length ?? 0,
                    {attributes: {engine: chatEngine}}
                );
                try {
                    const endpoint = getApiEndpoint();

                    // Get user's preferred language from settings cache, fall back to account language
                    const settingsState = chatApi.endpoints.getSettings.select(
                        undefined
                    )(getState() as RootState);
                    const userState = bffApi.endpoints.getUser.select(
                        undefined
                    )(getState() as RootState);
                    const accountLanguage = userState?.data?.spraakId
                        ? getApiLanguageCode(
                              getLangCodeFromEnum(
                                  userState.data.spraakId as LanguageEnum
                              )
                          )
                        : ApiLanguageCode.Norwegian;
                    const preferredLanguage =
                        settingsState?.data?.preferredLanguage ??
                        accountLanguage;

                    const body =
                        chatEngine === ChatEngine.Experimental
                            ? {
                                  query: message,
                                  threadId,
                                  sliceMessageId: messageId,
                                  responseType:
                                      stateResponseTypeToApiResponseType(
                                          stateResponseType
                                      ),
                                  preferredLanguage,
                              }
                            : {
                                  prompt: message,
                                  threadInfo: {
                                      id: threadId,
                                  },
                                  sliceMessageId: messageId,
                                  responseType:
                                      stateResponseTypeToApiResponseType(
                                          stateResponseType
                                      ),
                                  preferredLanguage,
                                  ...(threadSeed?.length && {
                                      threadSeed,
                                  }),
                              };

                    const response = await fetch(endpoint, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(body),
                        signal: streamController.signal,
                    });

                    if (!response.ok) {
                        throw new Error(
                            `Error ${response.status}: ${response.statusText}`
                        );
                    }
                    if (!response.body) {
                        throw new Error(
                            "ReadableStream not yet supported in this browser."
                        );
                    }
                    const reader = response.body.getReader();
                    const decoder = new TextDecoder("utf-8");
                    let done = false;

                    let partialChunk = "";

                    let delta = new Date();
                    while (!done) {
                        const {value, done: doneReading} = await reader.read();
                        done = doneReading;

                        // It seems that bytes in a chunk might be split over multiple reads
                        // So we need to decode the chunk and then split it into lines
                        const chunkValue = decoder.decode(value, {
                            stream: true,
                        });
                        const fullChunk = partialChunk + chunkValue;
                        const lines = fullChunk.split("\n");
                        partialChunk = lines.pop() || "";

                        for (const line of lines) {
                            // SSE frames can look like:
                            //   event: streamedchatmessage
                            //   data: { ... }
                            // or just a raw JSON line without any prefix.
                            // Skip SSE metadata (event:, id:, :-comments) and
                            // blanks, pass everything else (data: and plain
                            // JSON) through to the JSON parser.
                            const trimmed = line.trim();
                            if (!trimmed) continue;
                            if (
                                trimmed.startsWith("event:") ||
                                trimmed.startsWith("id:") ||
                                trimmed.startsWith(":")
                            ) {
                                continue;
                            }
                            const dataMsg = trimmed.startsWith("data:")
                                ? trimmed.slice(5).trimStart()
                                : trimmed;

                            // Check if the message is the end of the stream
                            if (dataMsg === "[STREAMDONE]") {
                                done = true;
                                break;
                            }

                            const parsed = getParsedMessage(dataMsg);
                            const messageType = messageTypeToEnum({...parsed});

                            const context: MessageHandlerContext = {
                                dispatch,
                                accumulatedText,
                                delta,
                                streamId,
                                onThreadIdReceived,
                            };

                            const result: MessageHandlerResult = handleMessage(
                                messageType,
                                parsed,
                                context
                            );

                            // Update context with any changes from the handler
                            if (result.accumulatedText !== undefined) {
                                if (
                                    !firstTokenEmitted &&
                                    accumulatedText.length === 0 &&
                                    result.accumulatedText.length > 0
                                ) {
                                    Sentry.metrics.distribution(
                                        "chat.first_token_latency_ms",
                                        performance.now() - streamStart,
                                        {
                                            unit: "millisecond",
                                            attributes: {engine: chatEngine},
                                        }
                                    );
                                    firstTokenEmitted = true;
                                }
                                accumulatedText = result.accumulatedText;
                            }
                            if (result.delta !== undefined) {
                                delta = result.delta;
                            }
                            if (result.accumulatedDispatched !== undefined) {
                                accumulatedDispatched =
                                    result.accumulatedDispatched;
                            }
                        }
                    }
                } catch (error) {
                    const classification = classifyStreamError(error);
                    if (classification === "aborted") {
                        // continue
                    } else if (classification === "network") {
                        console.warn(
                            "Stream interrupted by a network error:",
                            error
                        );
                        Sentry.addBreadcrumb({
                            category: "chat.stream",
                            level: "warning",
                            message:
                                error instanceof Error
                                    ? error.message
                                    : String(error),
                        });
                        Sentry.captureMessage(
                            "Chat stream interrupted by network error",
                            {
                                level: "warning",
                                tags: {feature: "chat.stream"},
                            }
                        );
                        stopReason = ChatStopReason.NetworkError;
                    } else {
                        console.error("Error in streaming:", error);
                        Sentry.captureException(error, {
                            tags: {feature: "chat.stream"},
                        });
                        stopReason = ChatStopReason.UnknownError;
                    }
                } finally {
                    // The stream is over — [STREAMDONE], error, or user abort.
                    // If no terminal message (StopMessage / ContentFilter
                    // error) finalized the assistant message, do it here so
                    // streaming state and a pending PostSearch never hang.
                    if (!accumulatedDispatched) {
                        dispatch(
                            finishAssistantMessage({
                                id: streamId,
                                text: accumulatedText,
                                stopReason,
                            })
                        );
                    }
                    streamControllers.delete(streamId);
                    Sentry.metrics.distribution(
                        "chat.stream_duration_ms",
                        performance.now() - streamStart,
                        {
                            unit: "millisecond",
                            attributes: {
                                engine: chatEngine,
                                stopReason:
                                    typeof stopReason === "number"
                                        ? ChatStopReason[stopReason]
                                        : stopReason,
                            },
                        }
                    );
                }

                // TODO
                // Burde gjøres sjeldnere, men dette gjør at vi ikke får feil data i UI
                // Kanskje injisere tråden inn i Threads?
                dispatch(
                    chatApi.util.invalidateTags([
                        "Threads",
                        "SearchThreads",
                        "Init",
                    ])
                );
                await queryFulfilled;
            },
        }),
        likeMessage: builder.mutation<void, {messageId: string}>({
            query: ({messageId}) => ({
                url: `like`, // TODO - Update when endpoint has been implemented
                method: "PATCH",
                body: messageId,
            }),
        }),
        dislikeMessage: builder.mutation<void, {messageId: string}>({
            query: ({messageId}) => ({
                url: `dislike`, // TODO - Update when endpoint has been implemented
                method: "PATCH",
                body: messageId,
            }),
        }),
        searchUserThreads: builder.query<
            PaginatedResponse<ThreadSearchResult>,
            {query: string; page: number; pageSize: number}
        >({
            query: ({query, page, pageSize}) => ({
                url: `/RedisThread/search?query=${query}&page=${page}&pageSize=${pageSize}`,
                method: "GET",
            }),
            transformResponse: (
                response: PaginatedResponse<ThreadSearchResultRaw>
            ) => {
                const json = {
                    ...response,
                    items: response.items.map((item) => ({
                        ...item,
                        matchingMessage: tryParseJson(item.matchingMessage),
                    })),
                };
                // For now we have no use of the markdown, so we strip it
                // If we need it in the future, we can add it back in and
                // remove it specifically in AppSpotlight.tsx
                json.items = json?.items?.map((item) => {
                    const content =
                        typeof item.matchingMessage === "string"
                            ? item.matchingMessage
                            : item.matchingMessage.content;
                    return {
                        ...item,
                        matchingMessage: {
                            ...item.matchingMessage,
                            content: remark()
                                .use(stripMarkdown)
                                .processSync(content)
                                .toString()
                                .replace(/\\/g, ""),
                        },
                    };
                });

                return json;
            },
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return `${endpointName}-${queryArgs.query}`;
            },
            // Facilitates infinite scroll
            merge: (currentCache, newItems) => {
                const existingItemsMap = new Map(
                    currentCache.items.map((item, index) => [
                        item.threadId,
                        {item, index},
                    ])
                );

                const itemsToAdd: typeof newItems.items = [];

                // Process new items: update existing or collect new ones
                newItems.items.forEach((newItem) => {
                    const existing = existingItemsMap.get(newItem.threadId);
                    if (existing) {
                        // Replace existing item in place
                        currentCache.items[existing.index] = newItem;
                    } else {
                        // Collect new items to append
                        itemsToAdd.push(newItem);
                    }
                });

                // Append new items to maintain pagination order
                currentCache.items.push(...itemsToAdd);
            },
            // Force refetch when the page number changes
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg?.page !== previousArg?.page;
            },

            providesTags: ["SearchThreads"],
        }),
        getUserThreads: builder.query<
            PaginatedResponse<ThreadInfo>,
            RedisPagedRequest
        >({
            query: ({page, pageSize}) => ({
                url: `/RedisThread?page=${page}&pageSize=${pageSize}`,
                method: "GET",
            }),
            keepUnusedDataFor: 600,
            // Since we merge the new items into the current cache, we only keep 1 cache entry regardless of the page number
            serializeQueryArgs: ({endpointName}) => {
                return endpointName;
            },
            // Facilitates infinite scroll
            merge: (currentCache, newItems) => {
                const existingItemsMap = new Map(
                    currentCache.items.map((item, index) => [
                        item.threadId,
                        {item, index},
                    ])
                );

                const itemsToAdd: typeof newItems.items = [];

                // Process new items: update existing or collect new ones
                newItems.items.forEach((newItem) => {
                    const existing = existingItemsMap.get(newItem.threadId);
                    if (existing) {
                        // Replace existing item in place
                        currentCache.items[existing.index] = newItem;
                    } else {
                        // Collect new items to append
                        itemsToAdd.push(newItem);
                    }
                });

                // Append new items to maintain pagination order
                currentCache.items.push(...itemsToAdd);
            },
            // Force refetch when the page number changes
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg?.page !== previousArg?.page;
            },
            providesTags: ["Threads"],
        }),
        getThread: builder.query<ThreadWithMetadata | undefined, string>({
            query: (threadId) => ({
                url: `/RedisThread/${threadId}`,
                method: "GET",
            }),
            providesTags: ["Thread"],
        }),
        deleteThread: builder.mutation<void, string>({
            query: (id) => ({
                url: `/RedisThread/${id}`,
                method: "DELETE",
            }),
            async onQueryStarted(id, {dispatch, queryFulfilled, getState}) {
                // Optimistically remove thread from getUserThreads cache
                const patchResult = dispatch(
                    chatApi.util.updateQueryData(
                        "getUserThreads",
                        {page: 0, pageSize: 20}, // These params don't matter due to serializeQueryArgs
                        (draft) => {
                            draft.items = draft.items.filter(
                                (thread) => thread.threadId !== id
                            );
                        }
                    )
                );

                // Optimistically remove thread from getInit cache (front-page list)
                const initPatchResult = dispatch(
                    chatApi.util.updateQueryData(
                        "getInit",
                        {threadCount: 3},
                        (draft) => {
                            draft.threads.items = draft.threads.items.filter(
                                (thread) => thread.threadId !== id
                            );
                        }
                    )
                );

                // Remove matching local entity so the sidebar (local-first) drops it too.
                const localEntity = threadsSelectors
                    .selectAll(getState() as RootState)
                    .find((t) => t.realThreadId === id || t.id === id);
                if (localEntity) {
                    dispatch(removeThread(localEntity.id));
                }

                try {
                    await queryFulfilled;
                } catch {
                    // If the deletion fails, undo the optimistic update
                    patchResult.undo();
                    initPatchResult.undo();
                }
            },
            invalidatesTags: ["SearchThreads", "Threads", "Thread", "Init"],
        }),
        renameThread: builder.mutation<void, {threadId: string; name: string}>({
            query: ({threadId, name}) => ({
                url: `/RedisThread/${threadId}/name`,
                method: "PATCH",
                body: {name},
            }),
            async onQueryStarted(
                {threadId, name},
                {dispatch, queryFulfilled, getState}
            ) {
                // Optimistically update thread name in getUserThreads cache
                const patchResult = dispatch(
                    chatApi.util.updateQueryData(
                        "getUserThreads",
                        {page: 0, pageSize: 20},
                        (draft) => {
                            const thread = draft.items.find(
                                (t) => t.threadId === threadId
                            );
                            if (thread) thread.name = name;
                        }
                    )
                );

                // Also update the local threads slice entry if we have it, so
                // the sidebar (which prefers local state) reflects the rename.
                const localEntity = threadsSelectors
                    .selectAll(getState() as RootState)
                    .find(
                        (t) => t.realThreadId === threadId || t.id === threadId
                    );
                const previousName = localEntity?.name;
                if (localEntity) {
                    dispatch(updateThreadName({id: localEntity.id, name}));
                }

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                    if (localEntity && previousName !== undefined) {
                        dispatch(
                            updateThreadName({
                                id: localEntity.id,
                                name: previousName,
                            })
                        );
                    }
                }
            },
            invalidatesTags: ["SearchThreads", "Threads", "Init"],
        }),
        rateMessage: builder.mutation<
            void,
            {threadId: string} & RateMessageRequest
        >({
            query: ({
                threadId,
                messagePosition,
                rating,
                comment,
                category,
            }) => ({
                url: `/RedisThread/${threadId}/rating`,
                method: "POST",
                body: {position: messagePosition, rating, comment, category},
            }),
        }),
        getIntercomInfo: builder.query<unknown, void>({
            query: () => ({
                url: `/intercominfo`,
                method: "GET",
            }),
        }),
        pong: builder.query<string, void>({
            query: () => ({
                url: `/semantic/ping`,
                method: "GET",
            }),
        }),
        postSearch: builder.query<PostSearchResponse, PostSearchRequest>({
            query: (params) => ({
                url: `/Search/PostSearch`,
                method: "POST",
                body: params,
            }),
            keepUnusedDataFor: 60,
        }),
        persistPostSearch: builder.mutation<
            void,
            {threadId: string} & ThreadPostSearch
        >({
            query: ({threadId, ...body}) => ({
                url: `/RedisThread/${threadId}/postsearch`,
                method: "POST",
                body,
            }),
        }),
        quickSearch: builder.query<QuickSearchResult[], QuickSearchRequest>({
            query: (params) => ({
                url: `/Search/QuickSearch`,
                method: "POST",
                body: params,
            }),
            // Keep default/empty query results cached for 5 minutes
            keepUnusedDataFor: 300,
            // Normalize empty queries to use same cache key for default results
            serializeQueryArgs: ({queryArgs}) => {
                const normalizedQuery = queryArgs.query?.trim() || "";
                return `quickSearch-${normalizedQuery}-${queryArgs.take}-${queryArgs.systemname}`;
            },
        }),
        containTriggerWord: builder.query<string[], string>({
            query: (query) => ({
                url: `/Search/ContainingTriggerWords?query=${encodeURIComponent(query)}`,
                method: "GET",
            }),
        }),
        triggerSearch: builder.mutation<
            TriggerSearchResult[],
            {query: string; take?: number}
        >({
            query: (params) => ({
                url: `/Search/TriggerSearch`,
                method: "POST",
                body: {
                    query: params.query,
                    take: params.take ?? 10,
                    systemname: "DIB",
                },
            }),
        }),
        getInit: builder.query<InitResponse, {threadCount: number}>({
            query: ({threadCount}) => ({
                url: `/Init?threadCount=${threadCount}`,
                method: "GET",
            }),
            providesTags: ["Init"],
            async onQueryStarted(_params, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;

                    // Populate getUserThreads cache
                    dispatch(
                        chatApi.util.upsertQueryData(
                            "getUserThreads",
                            {page: 1, pageSize: 30},
                            data.threads
                        )
                    );

                    // Populate quickSearch cache
                    dispatch(
                        chatApi.util.upsertQueryData(
                            "quickSearch",
                            {query: "", take: 8, systemname: "DIB"},
                            data.recentTopics
                        )
                    );

                    // Populate getSettings cache
                    dispatch(
                        chatApi.util.upsertQueryData(
                            "getSettings",
                            undefined,
                            data.settings
                        )
                    );
                } catch {
                    // If init fails, individual endpoints will be called as fallback
                }
            },
        }),
        getSettings: builder.query<UserSettingsResponse, void>({
            query: () => ({
                url: `/Settings`,
                method: "GET",
            }),
        }),
        updateSettings: builder.mutation<void, UpdateUserSettingsRequest>({
            query: (body) => ({
                url: `/Settings`,
                method: "PUT",
                body,
            }),
            async onQueryStarted(body, {dispatch, queryFulfilled}) {
                // Optimistically update the getSettings cache
                const patchResult = dispatch(
                    chatApi.util.updateQueryData(
                        "getSettings",
                        undefined,
                        (draft) => {
                            draft.preferredLanguage = body.preferredLanguage;
                        }
                    )
                );

                try {
                    await queryFulfilled;
                } catch {
                    // If the update fails, undo the optimistic update
                    patchResult.undo();
                }
            },
        }),

        /* customChatCompletion: builder.mutation<
        customChatCompletion: builder.mutation<
            any,
            {model: string; systemPrompt: string; query: string}
        >({
            query: ({model, systemPrompt, query}) => ({
                url: `CustomChatCompletion`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: {
                    model,
                    systemPrompt,
                    query,
                },
            }),
        }), */
    }),
});

export const {
    useGetThreadQuery,
    /* useCustomChatCompletionMutation, */
} = chatApi;
